import { MarketWatchRows, MarketWatchSymbolResponse, SearchSymbolRequest, SingleIndexQuotes } from './../../../../../app.interfaces';
import { Injectable } from '@angular/core';
import {
    AddRemoveMarketWatchSymbolRequest, RetrieveMarketWatch,
    MarketWatchQuotes, AddRemoveSymbolResponse, SingleQuoteParams,
    MarketWatchQuote, CreateMarketWatchParams, MarketWatchList,
    MarketWatchListResponse,
    EditMarketWatchResponse,
    RenameMarketWatchParams,
    SetCacheRequestParams,
    MarketWatchOrderResponse,
    CacheResponse
} from '../../../../../app.interfaces';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import { CACHE_KEYS } from '../../../../../_helpers/constants';
import { of, Subscription } from 'rxjs';
import 'rxjs/add/operator/map';
import { applicationUrls } from 'sharedweb/helpers/urls';
import { map, tap } from 'rxjs/operators';
import { MarketIDMap } from 'sharedweb/websocket.enums';

@Injectable({
    providedIn: 'root'
})
export class MarketWatchService {

    private _cachedMarketWatchData: MarketWatchList = {};
    private _cachedMarketWatchOrder: MarketWatchOrderResponse = {};
    private _cachedSymbolMap = new Map<string, MarketWatchSymbolResponse>();

    constructor(
        private _httpClient: HttpClient
    ) {
    }

    getMarketWatchData(marketWatchId: number) {

        if (this._cachedMarketWatchData[marketWatchId]
            && this._cachedMarketWatchData[marketWatchId].staticData
        ) {
            return of(this._cachedMarketWatchData[marketWatchId].staticData);
        } else {
            const dataUrl = `${applicationUrls.marketWatchUrl}${marketWatchId}/`;
            const dataResponse = this._httpClient.get<RetrieveMarketWatch>(dataUrl)
                .map(data => {
                    this._cachedMarketWatchData[marketWatchId] = {
                        marketWatchName: data.market_watch_name,
                    };
                    this._cachedMarketWatchData[marketWatchId].staticData = data;
                    if (data.saved_order) {
                        this._cachedMarketWatchOrder = JSON.parse(data.saved_order);
                    }
                    return this._cachedMarketWatchData[marketWatchId].staticData;
                });

            return dataResponse;
        }
    }

    getMarketWatchOrder() {
        if (this._cachedMarketWatchOrder) {
            return of(this._cachedMarketWatchOrder);
        } else {
            const orderUrl = applicationUrls.getCacheUrl;
            return this._httpClient.get<CacheResponse>(orderUrl, {
                params: {
                    key: CACHE_KEYS.marketWatchOrder
                }
            }).map(response => {
                if (response && response.data) {
                    const symbolOrder = JSON.parse(response.data);
                    this._cachedMarketWatchOrder = symbolOrder;
                    return this._cachedMarketWatchOrder;
                } else {
                    return {};
                }
            });
        }
    }

    getQuotesData(marketWatchId: number) {

        if (this._cachedMarketWatchData[marketWatchId] && this._cachedMarketWatchData[marketWatchId].quotesData) {
            return of(this._cachedMarketWatchData[marketWatchId].quotesData);
        } else {
            const url = `${applicationUrls.marketWatchUrl}${marketWatchId}/quotes/`;
            return this._httpClient.get<MarketWatchQuotes>(url)
                .map(data => {
                    this._cachedMarketWatchData[marketWatchId].quotesData = data;
                    return this._cachedMarketWatchData[marketWatchId].quotesData;
                });
        }
    }

    updateQuoteCache(quote: MarketWatchQuote | SingleIndexQuotes, marketWatchId: number) {
        if (quote && quote.symbol_id && this._cachedMarketWatchData[marketWatchId] &&
            this._cachedMarketWatchData[marketWatchId].quotesData) {
            this._cachedMarketWatchData[marketWatchId].quotesData[quote.symbol_id] = quote;
        }
    }

    addRemoveSymbol(addMarketWatchSymbolRequest: AddRemoveMarketWatchSymbolRequest, marketWatchId: number, symbolId?: number) {

        const url = `${applicationUrls.marketWatchUrl}${marketWatchId}/`;
        return this._httpClient.put<AddRemoveSymbolResponse>(url, addMarketWatchSymbolRequest)
            .map(data => {
                if (data.data.rows.length) {
                    if (this._cachedMarketWatchData[marketWatchId]) {
                        this._cachedMarketWatchData[marketWatchId].staticData.rows.push(data.data.rows[0]);
                    }
                } else {
                    const cachedData = this._cachedMarketWatchData[marketWatchId];
                    this._cachedMarketWatchData[marketWatchId].staticData.rows.forEach((item, index) => {
                        if (item.symbol && item.symbol.symbol_id === symbolId) {
                            this._cachedMarketWatchData[marketWatchId].staticData.rows.splice(index, 1);
                        }
                    });
                    this._cachedMarketWatchData[marketWatchId] = cachedData;
                }

                return data;
            });
    }

    createMarketWatch(createMarketWatchParams: CreateMarketWatchParams) {
        const url = applicationUrls.marketWatchUrl;
        return this._httpClient.post<AddRemoveSymbolResponse>(url, createMarketWatchParams);
    }

    listMarketWatch() {
        const url = applicationUrls.marketWatchUrl;

        return this._httpClient.get<RetrieveMarketWatch[]>(url)
            .map(data => {
                data.forEach(row => {
                        // Market watch list now contains all the symbols of that MW. Caching those results
                        // so that no requests are made to web-server for each request (prevents loading screen)
                        this._cachedMarketWatchData[row.id] = {
                            marketWatchName: row.market_watch_name,
                        };
                        this._cachedMarketWatchData[row.id].staticData = row;
                        if (row.saved_order) {
                            this._cachedMarketWatchOrder = JSON.parse(row.saved_order);
                        }
                        this.setMarketWatchSymbolsCache(row.rows ?? []);
                })

                return data
            });
    }

    setMarketWatchSymbolsCache(marketWatchRows: MarketWatchRows[]) {
        marketWatchRows.forEach(row => {
            const symbol = row.symbol;
            const newSymbolKey = this.getSymbolKey(symbol.security_id, MarketIDMap[symbol.market_id]);
            this._cachedSymbolMap.set(newSymbolKey, symbol);
        });
    }

    deleteMarketWatch(marketWatchId: number) {
        const url = `${applicationUrls.marketWatchUrl}${marketWatchId}/`;
        return this._httpClient.delete<EditMarketWatchResponse>(url);
    }

    renameMarketWatch(marketWatchId: number, renameMarketWatchParams: RenameMarketWatchParams) {
        const url = `${applicationUrls.marketWatchUrl}${marketWatchId}/`;
        return this._httpClient.put<EditMarketWatchResponse>(url, renameMarketWatchParams);
    }

    setMarketWatchOrder(setMarketWatchOrder: SetCacheRequestParams) {
        const url = applicationUrls.setCacheUrl;
        this._cachedMarketWatchOrder = JSON.parse(setMarketWatchOrder.data);
        return this._httpClient.post<any>(url, setMarketWatchOrder);
    }

    getSymbolKey(security_id: number, market_id: number) {
        return market_id + "_" + security_id;
    }

    getMWStaticData(searchSymbolRequest: SearchSymbolRequest) {
        if (searchSymbolRequest.security_id && searchSymbolRequest.market_id) {
            const symbolKey = this.getSymbolKey(searchSymbolRequest.security_id, MarketIDMap[searchSymbolRequest.market_id]);
            if (this._cachedSymbolMap.has(symbolKey)) {
                return of({ static_data: this._cachedSymbolMap.get(symbolKey) });
            }
            return this._httpClient.get<any>(`${applicationUrls.singleSymbolData}`, {
                params: <any>searchSymbolRequest
            }).pipe(
                tap(response => {
                    const symbolKey = this.getSymbolKey(searchSymbolRequest.security_id, MarketIDMap[searchSymbolRequest.market_id]);
                    this._cachedSymbolMap.set(symbolKey, response && response.static_data);
                }),
                map(response => {
                    return response;
                })
            );
        } 
        else if(searchSymbolRequest.symbol_id) {
            if(this._cachedSymbolMap.has(searchSymbolRequest.symbol_id.toString())) {
                return of({ static_data: this._cachedSymbolMap.get(searchSymbolRequest.symbol_id.toString()) });
            }
            return this._httpClient.get<any>(`${applicationUrls.singleSymbolData}`, {
                params: <any>searchSymbolRequest
            }).pipe(
                tap(response => {
                    const symbolKey = searchSymbolRequest.symbol_id.toString();
                    this._cachedSymbolMap.set(symbolKey, response && response.static_data);
                }),
                map(response => {
                    return response;
                })
            );
        }
        else {
            return of(null);
        }
    }
}
